import { useState } from 'react';
import { Loader2, Check, FileText, ArrowLeftRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { matchingAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';
import { format } from 'date-fns';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(new Date(d), 'dd MMM yyyy'); } catch (e) { return String(d); }
}

/**
 * Expandable preview of exactly what "Confirm all high-confidence" will
 * confirm, gated behind one click. The candidate list is fetched by the
 * caller (ReconciliationPage, via matchingAPI.highConfidencePreview) so the
 * button label count and this dialog always agree; applying POSTs the same
 * match_ids the operator reviewed here, not a re-derived set.
 */
export function HighConfidenceBulkConfirmDialog({ open, candidates, onClose, onApplied }) {
  var [applying, setApplying] = useState(false);

  var handleConfirmAll = function () {
    if (!candidates || candidates.length === 0) return;
    setApplying(true);
    var matchIds = candidates.map(function (c) { return c.match_id; });
    matchingAPI.bulkConfirmHighConfidence(matchIds)
      .then(function (res) {
        var data = res.data || {};
        var confirmedCount = data.confirmed_count || 0;
        var skippedCount = data.skipped_count || 0;
        if (confirmedCount > 0) {
          var msg = 'Confirmed ' + confirmedCount + ' match' + (confirmedCount !== 1 ? 'es' : '');
          if (skippedCount > 0) {
            msg += ' (' + skippedCount + ' skipped, see details)';
          }
          toast.success(msg);
        }
        if (skippedCount > 0 && confirmedCount === 0) {
          toast.error('All ' + skippedCount + ' candidate(s) were skipped -- they no longer qualify.');
        }
        setApplying(false);
        onApplied && onApplied(data);
      })
      .catch(function (err) {
        setApplying(false);
        toast.error(getErrorMessage(err));
      });
  };

  var candidateList = candidates || [];

  return (
    <Dialog open={open} onOpenChange={function (o) { if (!o && !applying) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="high-confidence-bulk-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Confirm all high-confidence ({candidateList.length})</DialogTitle>
          <DialogDescription>
            Every candidate below has an exact amount match, a real counterparty match, and a
            transaction date close to the invoice due date. Confirming stamps each invoice as
            paid, exactly as an individual Confirm would.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-80 overflow-y-auto border rounded-lg divide-y" data-testid="high-confidence-preview-list">
          {candidateList.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No high-confidence candidates right now.
            </p>
          ) : (
            candidateList.map(function (c) {
              return (
                <div key={c.match_id} className="p-3 text-sm flex items-center justify-between gap-3" data-testid={'high-confidence-row-' + c.match_id}>
                  <span className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <span className="truncate">
                      <span className="font-mono text-xs">{c.invoice_number || '—'}</span>
                      <span className="text-muted-foreground"> · {c.supplier_client_name || c.counterparty || '—'}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0 text-muted-foreground">
                    <ArrowLeftRight className="h-3.5 w-3.5" />
                    <span>{fmtDate(c.transaction_date)}</span>
                  </span>
                  <span className="font-mono text-sm shrink-0">{formatCurrency(c.amount, c.currency)}</span>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button
            onClick={handleConfirmAll}
            disabled={candidateList.length === 0 || applying}
            className="bg-green-600 hover:bg-green-700"
            data-testid="high-confidence-confirm-all-btn"
          >
            {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            Confirm all ({candidateList.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
